import { ModalsState } from "@/zustand/slices/modals";
import { useStore } from "@/zustand/store";
import { CommentRounded } from "@mui/icons-material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import { LoadingButton, LoadingButtonProps } from "@mui/lab";
import { IconButton, IconButtonProps, Tooltip } from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const EditButton = (props: IconButtonProps) => {
  const { t } = useTranslation();

  return (
    <Tooltip title={<>{t("editing.edit")}</>}>
      <span style={props.disabled ? { cursor: "not-allowed" } : undefined}>
        <IconButton {...props}>
          <EditRoundedIcon />
        </IconButton>
      </span>
    </Tooltip>
  );
};

const CommentButton = (props: IconButtonProps) => {
  const { t } = useTranslation();

  return (
    <Tooltip title={<>{t("editing.comment")}</>}>
      <span style={props.disabled ? { cursor: "not-allowed" } : undefined}>
        <IconButton {...props}>
          <CommentRounded />
        </IconButton>
      </span>
    </Tooltip>
  );
};

const DeleteButton = (props: IconButtonProps) => {
  const { t } = useTranslation();

  return (
    <Tooltip title={<>{t("editing.delete")}</>}>
      <span style={props.disabled ? { cursor: "not-allowed" } : undefined}>
        <IconButton {...props}>
          <DeleteRoundedIcon />
        </IconButton>
      </span>
    </Tooltip>
  );
};

type AddInModalButtonProps = IconButtonProps<"label"> & {
  modalId: keyof ModalsState;
};

const AddInModalButton = (props: AddInModalButtonProps) => {
  const { modalId, ...rest } = props;

  const { t } = useTranslation();

  const { openModal } = useStore((state) =>
    state.getModalStateChangeFns(modalId)
  );

  const handleAddButtonClick = (
    e: React.MouseEvent<HTMLLabelElement, MouseEvent>
  ) => {
    e.stopPropagation();
    openModal();
  };

  return (
    <Tooltip title={<>{t("editing.add")}</>}>
      <span style={props.disabled ? { cursor: "not-allowed" } : undefined}>
        <IconButton {...rest} onClick={handleAddButtonClick} component="label">
          <AddRoundedIcon />
        </IconButton>
      </span>
    </Tooltip>
  );
};

export type EditInModalButtonProps<TModalId extends keyof ModalsState> =
  IconButtonProps & {
    modalId: TModalId;
    modalEditData: ModalsState[TModalId]["data"];
  };

const EditInModalButton = <TModalId extends keyof ModalsState>(
  props: EditInModalButtonProps<TModalId>
) => {
  const { modalId, modalEditData, ...rest } = props;

  const { openModal } = useStore((state) =>
    state.getModalStateChangeFns(modalId)
  );

  const handleEditButtonClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation();
    openModal(modalEditData);
  };

  return <EditButton {...rest} onClick={handleEditButtonClick} />;
};

export type EditCommentInModalButtonProps<TModalId extends keyof ModalsState> =
  IconButtonProps & {
    modalId: TModalId;
    modalEditData: ModalsState[TModalId]["data"];
  };

const EditCommentInModalButton = <TModalId extends keyof ModalsState>(
  props: EditInModalButtonProps<TModalId>
) => {
  const { modalId, modalEditData, ...rest } = props;

  const { openModal } = useStore((state) =>
    state.getModalStateChangeFns(modalId)
  );

  const handleEditButtonClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation();
    openModal(modalEditData);
  };

  return <CommentButton {...rest} onClick={handleEditButtonClick} />;
};

export type DeleteWithConfirmButtonProps = Omit<IconButtonProps, "onClick"> & {
  confirmTitle?: string;
  confirmDescription: string;
  deleteFn: () => void;
  redirect?: string;
};

const DeleteWithConfirmButton = (props: DeleteWithConfirmButtonProps) => {
  const { confirmTitle, confirmDescription, deleteFn, redirect, ...rest } =
    props;

  const { t } = useTranslation();

  const confirm = useConfirm();

  const navigate = useNavigate();

  const handleDeleteButtonClick = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation();
    try {
      await confirm({
        title: confirmTitle ?? t("dialogs.deleteTitle"),
        description: confirmDescription,
      });
      if (redirect) {
        navigate(redirect, { replace: true });
      }
      deleteFn();
    } catch {
      return;
    }
  };

  return <DeleteButton {...rest} onClick={handleDeleteButtonClick} />;
};

const VerifyButton = (props: LoadingButtonProps) => {
  return <LoadingButton {...props} endIcon={<CheckCircleRoundedIcon />} />;
};

const BooleanButton = ({
  condition,
  ...rest
}: LoadingButtonProps & { condition: boolean }) => (
  <LoadingButton
    {...rest}
    size={rest.size ?? "small"}
    color={condition ? "error" : "success"}
    endIcon={condition ? <CloseRoundedIcon /> : <CheckRoundedIcon />}
  />
);

export {
  DeleteButton,
  EditButton,
  DeleteWithConfirmButton,
  EditInModalButton,
  EditCommentInModalButton,
  AddInModalButton,
  VerifyButton,
  BooleanButton,
};
